/**
 * A lookup run's steps (piece 16, *Manufacturer lookup*; decisions 12660, 12661):
 * the lines one Item's step draws, found beside held, the answers a person
 * gives, and the views the Step Through window reads. 🔨 DOM-free.
 */
import { fill, t } from '../strings';
import {
  LOOKUP_FIELDS, LOOKUP_FIELD_LABELS, MANUFACTURER_LIFECYCLE_LABELS, isManufacturerLifecycle, lifecycleFromStated,
  type LookupField, type LookupProposal, type LookupReadBy,
} from './manufacturerLookup';

/** The kinds of line one step draws, in the order it draws them. */
export const LOOKUP_LINE_KINDS = ['field', 'attribute', 'document', 'picture'] as const;
export type LookupLineKind = (typeof LOOKUP_LINE_KINDS)[number];

/** How a found attribute meets the catalog's types, resolved by the server against the Item. */
export interface LookupAttributeMatch {
  /** The attribute type of that name; null proposes a new one, made only on accept. */
  attributeTypeId: string | null;
  /** The new type's kind and unit, where one is proposed. */
  newKind: 'Measured' | 'Enumerated' | null;
  /** What the Item holds for the type, as it reads; null for nothing. */
  held: string | null;
  /** The sentence a value the type cannot take is refused with; null where it can take it. */
  refusal: string | null;
}

/** What an Item already holds, read once per step (decision 12660's re-import rule). */
export interface LookupHeld {
  fields: Readonly<Record<LookupField, string | null>>;
  /** Each found attribute's match, by its place in the proposal. */
  attributes: readonly LookupAttributeMatch[];
  /** Each found document's held twin, a file on the Item of the same title and kind, by its place; null where none. */
  documents: readonly (string | null)[];
  /** Each found document's refusal, a kind naming no File Type here; null where it names one. */
  documentRefusals: readonly (string | null)[];
  hasImage: boolean;
}

/** One line of one step: what was found, what the Item holds, and whether it is ticked to be taken. */
export interface LookupStepLine {
  key: string;
  kind: LookupLineKind;
  label: string;
  found: string;
  held: string | null;
  /** The found value is what the Item already holds, so the line has nothing to take. */
  same: boolean;
  /** 🔒 Ticked only where the Item holds nothing and the line can be taken (decision 12660: adds, never overwrites). */
  take: boolean;
  /** Whether the person can edit the found value before taking it. */
  editable: boolean;
  /** The price line is money, drawn through the one formatter. */
  money: boolean;
  readBy: LookupReadBy;
  confidence: number | null;
  /** A line that cannot be taken, with why; null where it can be. */
  refusal: string | null;
  /** An attribute line proposing a new attribute type, made only on accept. */
  newType: 'Measured' | 'Enumerated' | null;
  /** A document's or a picture's file in the bundle. */
  file: string | null;
  itemImage: boolean;
}

function same(found: string, held: string | null, money: boolean): boolean {
  if (held === null) return false;
  if (money) return Number(found) === Number(held);
  return found.trim() === held.trim();
}

function lineOf(base: Omit<LookupStepLine, 'take'>): LookupStepLine {
  return { ...base, take: !base.same && base.held === null && base.refusal === null };
}

/** The refusal a field's found value meets on its own, before any Item is read. */
export function lookupFieldRefusal(field: LookupField, value: string): string | null {
  if (field === 'listPrice') {
    const amount = Number(value.replace(/[$,\s]/g, ''));
    return Number.isFinite(amount) && amount >= 0 ? null : fill(t('itemLookup.refusal.price'), { value });
  }
  if (field === 'lifecycle') return lifecycleFromStated(value) ? null : fill(t('itemLookup.refusal.lifecycle'), { value });
  return null;
}

/** A price as it is stored: the number alone, the dollar sign and the commas dropped. */
export function lookupPriceOf(value: string): number {
  return Number(value.replace(/[$,\s]/g, ''));
}

/**
 * 🔒 The step's lines (decision 12660): every found value beside what the Item
 * holds. A line is ticked only where the Item holds nothing for it and it can be
 * taken, so a held value changes only where a person ticks it; a found value the
 * Item already holds reads as the same and has nothing to take.
 */
export function lookupStepLines(proposal: LookupProposal, held: LookupHeld): LookupStepLine[] {
  const lines: LookupStepLine[] = [];
  for (const field of LOOKUP_FIELDS) {
    const found = proposal.fields[field];
    if (!found) continue;
    const money = field === 'listPrice';
    const lifecycle = field === 'lifecycle' ? lifecycleFromStated(found.value) : null;
    const shown = lifecycle ? t(MANUFACTURER_LIFECYCLE_LABELS[lifecycle]) : found.value;
    const heldValue = held.fields[field];
    const heldShown = field === 'lifecycle' && isManufacturerLifecycle(heldValue) ? t(MANUFACTURER_LIFECYCLE_LABELS[heldValue]) : heldValue;
    lines.push(lineOf({
      key: `field:${field}`, kind: 'field', label: t(LOOKUP_FIELD_LABELS[field]), found: money ? String(lookupPriceOf(found.value)) : shown, held: heldShown,
      same: field === 'lifecycle' ? lifecycle !== null && lifecycle === heldValue : same(money ? String(lookupPriceOf(found.value)) : found.value, heldValue, money),
      editable: field !== 'lifecycle', money, readBy: found.readBy, confidence: found.confidence, refusal: lookupFieldRefusal(field, found.value), newType: null, file: null, itemImage: false,
    }));
  }
  proposal.attributes.forEach((found, index) => {
    const match = held.attributes[index] ?? { attributeTypeId: null, newKind: null, held: null, refusal: null };
    const value = found.unit ? `${found.value} ${found.unit}` : found.value;
    lines.push(lineOf({
      key: `attribute:${index}`, kind: 'attribute', label: found.name, found: value, held: match.held, same: same(value, match.held, false),
      editable: true, money: false, readBy: found.readBy, confidence: found.confidence, refusal: match.refusal, newType: match.attributeTypeId === null ? match.newKind : null, file: null, itemImage: false,
    }));
  });
  proposal.documents.forEach((found, index) => {
    lines.push(lineOf({
      key: `document:${index}`, kind: 'document', label: found.title, found: found.kind ? `${found.title} · ${found.kind}` : found.title, held: held.documents[index] ?? null, same: false,
      editable: false, money: false, readBy: found.readBy, confidence: found.confidence, refusal: held.documentRefusals[index] ?? null, newType: null, file: found.file, itemImage: false,
    }));
  });
  proposal.pictures.forEach((found, index) => {
    lines.push(lineOf({
      key: `picture:${index}`, kind: 'picture', label: found.file, found: found.itemImage ? t('itemLookup.picture.itemImage') : t('itemLookup.picture.gallery'),
      held: found.itemImage && held.hasImage ? t('itemLookup.picture.imageHeld') : null, same: false,
      editable: false, money: false, readBy: found.readBy, confidence: found.confidence, refusal: null, newType: null, file: found.file, itemImage: found.itemImage,
    }));
  });
  return lines;
}

/** 🔒 A person's answer on one line: taken or left, and the value as edited where it was. */
export interface LookupLineAnswer {
  take: boolean;
  value?: string;
}

export type LookupStepAnswers = Readonly<Record<string, LookupLineAnswer>>;

/** The answers a step opens on: each line as its default tick leaves it. */
export function defaultLookupAnswers(lines: readonly LookupStepLine[]): Record<string, LookupLineAnswer> {
  return Object.fromEntries(lines.map((line) => [line.key, { take: line.take }]));
}

/** Where a staged lookup row stands: waiting, walked with its answers saved, accepted, or skipped. */
export const LOOKUP_STEP_STATES = ['Waiting', 'Walked', 'Accepted', 'Skipped'] as const;
export type LookupStepState = (typeof LOOKUP_STEP_STATES)[number];

/** One step of a lookup run as the run's list reads it: the Item and where its step stands. */
export interface LookupStepSummary {
  rowId: string;
  itemId: string;
  label: string;
  state: LookupStepState;
  /** The sentence a write refused the step with; null where none. */
  refusal: string | null;
}

/** One step as the window draws it: its lines, read against the Item as it stands now while the step is undecided. */
export interface LookupStepView extends LookupStepSummary {
  source: string | null;
  lines: LookupStepLine[];
  /** The answers saved when the step was walked or decided; null on a step nobody has walked. */
  answers: LookupStepAnswers | null;
}

/** A lookup run, as the window reads it. */
export interface LookupRunView {
  runId: string;
  status: 'Staged' | 'Finished' | 'Cancelled';
  /** The bundle's file name, or the lookup's query in a phrase. */
  origin: string;
  steps: LookupStepSummary[];
  /** 🔒 Accept All waits until every undecided step has been walked (decision 12661). */
  acceptAllReady: boolean;
}

/** A lookup's answer to Look Up Manufacturer: a staged run, or the sentence saying why no page was read. */
export type LookupStartAnswer = { ok: true; runId: string } | { ok: false; refusal: string };

/** What Look Up Manufacturer asks the seam: the manufacturer and part number, or a pasted product page address. */
export interface LookUpRequestBody {
  manufacturer?: string | null;
  partNumber?: string | null;
  address?: string | null;
}

/** The routes (registered in the web's declared map the same commit). */
export const LOOKUP_BUNDLES_PATH = '/catalog/lookup-bundles';
export const lookupRunPath = (runId: string): string => `/catalog/lookup-runs/${encodeURIComponent(runId)}`;
export const lookupRunFilePath = (runId: string, file: string): string => `${lookupRunPath(runId)}/files/${encodeURIComponent(file)}`;
export const lookupRowPath = (runId: string, rowId: string): string => `${lookupRunPath(runId)}/rows/${encodeURIComponent(rowId)}`;
export const lookupStepPath = (runId: string, rowId: string, act: 'walk' | 'accept' | 'skip'): string => `${lookupRowPath(runId, rowId)}/${act}`;
export const lookupRunActPath = (runId: string, act: 'accept-all' | 'close'): string => `${lookupRunPath(runId)}/${act}`;
export const itemLookUpPath = (itemId: string): string => `/catalog/items/${encodeURIComponent(itemId)}/look-up`;
